import { queryOptions } from "@tanstack/react-query";
import { axiosInstance } from "./axios";
import { z } from "zod";

const playerKpiSchema = z.object({
    playerName: z.string(),
    kpi: z.number(),
});

const teamInfoSchema = z.object({
    teamId: z.number(),
    teamName: z.string(),
    sumKpi: z.number(),
    avgKpi: z.number(),
    goals: z.number(),
    assists: z.number().optional(),
    passes: z.number(),
    shots: z.number(),
    totalShots: z.number().optional(),
    accurateShots: z.number(),
    shotAccuracy: z.number(),
    shotAccuracyPercent: z.number().optional(),
    crosiFor: z.number(),
    corsiForPercent: z.number().optional(),
    fanwickFor: z.number(),
    fenwickForPercent: z.number().optional(),
    playersKpi: z.array(playerKpiSchema),
    goalkeepersKpi: z.array(playerKpiSchema),
});

const teamCompareSchema = z.object({
    matchId: z.number(),
    chosenInfo: teamInfoSchema,
    opponentInfo: teamInfoSchema,
});

export type PlayerKpi = z.infer<typeof playerKpiSchema>;
export type TeamInfo = z.infer<typeof teamInfoSchema>;
export type TeamCompareResponse = z.infer<typeof teamCompareSchema>;

const normalizeMatchIds = (matchIds?: number | number[]): number[] => {
    if (matchIds === undefined || matchIds === null) return [];
    return Array.isArray(matchIds) ? matchIds : [matchIds];
};

export const fetchTeamCompare = async (
    matchIds?: number | number[],
    teamId?: number
): Promise<TeamCompareResponse[]> => {
    const matchIdsArray = normalizeMatchIds(matchIds);

    try {
        const response = await axiosInstance.post("/matches/team/compare", { matchIds: matchIdsArray, teamId });

        if (!response.data) {
            return [];
        }

        const parsedData = teamCompareSchema.parse(response.data);
        return [parsedData];
    } catch (error) {
        const idsText = matchIdsArray.length > 0 ? ` for IDs ${matchIdsArray.join(", ")}` : "";
        const teamText = teamId ? ` and team ID ${teamId}` : "";
        console.error(`Failed to fetch team comparison data${idsText}${teamText}:`, error);
        throw new Error(`Failed to fetch team comparison data${idsText}${teamText}: ${error}`);
    }
};

export const teamCompareQueryOptions = (matchIds?: number | number[], teamId?: number) =>
    queryOptions({
        queryKey: ["team-compare", matchIds, teamId],
        queryFn: () => fetchTeamCompare(matchIds, teamId),
        staleTime: 5 * 60 * 1000,
    });
